import {
  SpecialJobEditRequest,
  SpecialJobsCreateRequest,
} from '~/common/entities/SpecialJobs/SpecialJobRequest';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import axios from 'axios';

import { SpecialJob } from '~/common/entities/SpecialJobs/SpecialJobsTypes';

import { IdProvider, unwrap } from '../common/apiCommons';

export enum SpecialJobKeys {
  SPECIAL_JOBS = 'SPECIAL_JOBS',
  SPECIAL_JOB = 'SPECIAL_JOB_',
}

const SpecialJobQueries = {
  getSpecialJobs(start?: string, end?: string) {
    if (start && end) {
      return axios
        .get<SpecialJob[]>(`/special_jobs/?start=${start}&end=${end}`)
        .then(unwrap);
    } else {
      return axios.get<SpecialJob[]>(`/special_jobs/`).then(unwrap);
    }
  },
  getSpecialJob(specialJob: IdProvider) {
    return axios
      .get<SpecialJob>(`/special_job/${specialJob?.id}/`)
      .then(unwrap);
  },
};

const SpecialJobMutations = {
  createSpecialJob(req: SpecialJobsCreateRequest) {
    return axios.post<SpecialJob>('/special_job/', req).then(unwrap);
  },
  editSpecialJob(specialJob: IdProvider, req: SpecialJobEditRequest) {
    return axios
      .put<SpecialJob>(`/special_job/${specialJob?.id}/`, req)
      .then(unwrap);
  },
  deleteSpecialJob(specialJob: IdProvider) {
    return axios.delete<void>(`/special_job/${specialJob?.id}/`).then(unwrap);
  },
  specialJobDone(specialJob: IdProvider) {
    return axios
      .post<SpecialJob>(`/special_job/${specialJob?.id}/done/`)
      .then(unwrap);
  },
  endSpecialJob(
    specialJob_id: string,
    employee_id: string,
    totalSeconds: number,
    doneAmount: number,
  ) {
    return axios
      .post(`/special_job/end/`, null, {
        params: {
          employeeId: employee_id,
          specialJobId: specialJob_id,
          totalSeconds: totalSeconds,
          doneAmount: doneAmount,
        },
      })
      .then(unwrap);
  },
  editSpecialJobInstance(
    specialJob_id: string,
    employee_id: string,
    totalSeconds: number,
    description: string,
  ) {
    return axios
      .post(`/special_job/edit_instance/`, null, {
        params: {
          employeeId: employee_id,
          specialJobId: specialJob_id,
          totalSeconds: totalSeconds,
          description: description,
        },
      })
      .then(unwrap);
  },
};

const SpecialJobHooks = {
  useSpecialJobsQuery(
    options?: UseQueryOptions<SpecialJob[]> & { start?: string; end?: string },
  ) {
    return useQuery({
      queryKey: [SpecialJobKeys.SPECIAL_JOBS, options?.start, options?.end],
      queryFn: () =>
        SpecialJobQueries.getSpecialJobs(options?.start, options?.end),
      ...options,
    });
  },
  useSpecialJobQuery(
    specialJob: IdProvider,
    options?: UseQueryOptions<SpecialJob>,
  ) {
    return useQuery({
      queryKey: [SpecialJobKeys.SPECIAL_JOB, specialJob?.id],
      queryFn: () => SpecialJobQueries.getSpecialJob(specialJob),
      ...options,
    });
  },
  useCreateSpecialJobMutation() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
      mutationFn: SpecialJobMutations.createSpecialJob,
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([SpecialJobKeys.SPECIAL_JOBS]);
        await queryClient.invalidateQueries([
          SpecialJobKeys.SPECIAL_JOB,
          data.id,
        ]);
      },
    });
    return mutation;
  },
  useEditSpecialJobMutation() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
      mutationFn: ({ id, ...rest }: IdProvider & SpecialJobEditRequest) =>
        SpecialJobMutations.editSpecialJob({ id }, rest),
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([SpecialJobKeys.SPECIAL_JOBS]);
        await queryClient.invalidateQueries([
          SpecialJobKeys.SPECIAL_JOB,
          data.id,
        ]);
      },
    });
    return mutation;
  },
  useDeleteJobMutation() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
      mutationFn: SpecialJobMutations.deleteSpecialJob,
      onSuccess: async () => {
        await queryClient.invalidateQueries([SpecialJobKeys.SPECIAL_JOBS]);
      },
    });
    return mutation;
  },
  useSpecialJobDoneMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: SpecialJobMutations.specialJobDone,
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([
          SpecialJobKeys.SPECIAL_JOB,
          data.id,
        ]);
        await queryClient.invalidateQueries([SpecialJobKeys.SPECIAL_JOBS]);
      },
    });
  },
  useEndSpecialJobMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (args: {
        specialJob_id: string;
        employee_id: string;
        totalSeconds: number;
        doneAmount: number;
      }) =>
        SpecialJobMutations.endSpecialJob(
          args.specialJob_id,
          args.employee_id,
          args.totalSeconds,
          args.doneAmount,
        ),
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([
          SpecialJobKeys.SPECIAL_JOB,
          data.id,
        ]);
        await queryClient.invalidateQueries([SpecialJobKeys.SPECIAL_JOBS]);
      },
    });
  },
  useEditSpecialJobInstanceMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (args: {
        specialJob_id: string;
        employee_id: string;
        totalSeconds: number;
        description: string;
      }) =>
        SpecialJobMutations.editSpecialJobInstance(
          args.specialJob_id,
          args.employee_id,
          args.totalSeconds,
          args.description,
        ),
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([
          SpecialJobKeys.SPECIAL_JOB,
          data.id,
        ]);
        await queryClient.invalidateQueries([SpecialJobKeys.SPECIAL_JOBS]);
      },
    });
  },
};

const SpecialJobApi = {
  queries: SpecialJobQueries,
  mutations: SpecialJobMutations,
  hooks: SpecialJobHooks,
};

export default SpecialJobApi;
